import {downloadDVIR} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {BaseQueryApi} from "@reduxjs/toolkit/query";

export type UseDownloadDVIRProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadDVIRResult = {
  downloadDVIR: (id: string) => void
}

export const useDownloadDVIR = (props: UseDownloadDVIRProps): UseDownloadDVIRResult => {
  const {baseQueryApi} = props

  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_downloading, _setDownloading] = useDebouncedState(false, 500)

  useEffect(() => {
    _setDownloading(_downloading)
  }, [_downloading, _setDownloading])


  useEffect(() => {
    if (_downloading) {
      notifications.show({
        id: "useDownloadDVIR_downloading",
        title: t("common.status.downloading", "Downloading") + " " + t("common.entity.dvir", "DVIR"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadDVIR_downloading")
    }
  }, [_downloading, t]);

  const _handleError = useCallback((rejected: any) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _downloadDVIR = useCallback((id: string) => {
    _setDownloading(true)

    downloadDVIR(id, regionId, baseQueryApi)
      .then((response) => {
        response.text()
          .then((url) => {
            _setDownloading(false)

            const a = document.createElement('a');
            a.href = url;
            a.target = "_blank"
            a.click();

            notifications.show({
              color: "green",
              title: t("common.entity.dvir", "DVIR") + " " + t("common.status.downloaded", "Downloaded"),
              message: ""
            });
          })
          .catch((rejected) => {
            _handleError(rejected)
          })
          .finally(() => {
            _setDownloading(false)
          })
      })
      .catch((rejected) =>
        _handleError(rejected)
      )
      .finally(() => {
        _setDownloading(false)
      })
  }, [regionId, _setDownloading, baseQueryApi, t, _handleError])

  return {
    downloadDVIR: _downloadDVIR,
  }
}
